import React, { useState } from 'react';
import { makeStyles, Theme } from '@material-ui/core';
import { colors } from '../../../theme/tokens';

const CORAL = colors.brandStrong;

const useStyles = makeStyles((theme: Theme) => ({
  wrap: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minWidth: 0,
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 10,
  },
  title: {
    fontSize: 11,
    fontWeight: 700,
    color: colors.textFaint,
    textTransform: 'uppercase' as any,
    letterSpacing: '0.06em',
  },
  card: {
    background: colors.bgSurface,
    borderRadius: 12,
    border: `1px solid ${colors.border}`,
    boxShadow: `0 1px 3px ${colors.shadow}`,
    flex: 1,
    overflow: 'hidden',
    boxSizing: 'border-box' as any,
  },
  row: {
    padding: '10px 16px',
    borderBottom: `1px solid ${colors.border}`,
    '&:last-child': { borderBottom: 'none' },
  },
  emptyText: {
    fontSize: 13,
    color: colors.textDisabled,
    textAlign: 'center' as any,
    padding: '24px 16px',
  },
  loadMoreBtn: {
    display: 'block',
    width: '100%',
    padding: '8px 10px',
    fontSize: 11,
    fontWeight: 700,
    color: CORAL,
    background: colors.brandTintSoft,
    border: 'none',
    borderTop: `1px solid ${colors.border}`,
    cursor: 'pointer',
  },
}));

export interface SideListCardProps<T> {
  title: string;
  items: T[];
  renderItem: (item: T, index: number) => React.ReactNode;
  pageSize?: number;
  emptyText?: string;
  headerRight?: React.ReactNode;
}

export default function SideListCard<T>({
  title,
  items,
  renderItem,
  pageSize = 5,
  emptyText = 'Nothing to show yet',
  headerRight,
}: SideListCardProps<T>) {
  const classes = useStyles();
  const [visibleCount, setVisibleCount] = useState(pageSize);
  const visible = items.slice(0, visibleCount);
  const hasMore = visibleCount < items.length;

  return (
    <div className={classes.wrap}>
      <div className={classes.headerRow}>
        <div className={classes.title}>{title}</div>
        {headerRight}
      </div>
      <div className={classes.card}>
        {items.length === 0 ? (
          <div className={classes.emptyText}>{emptyText}</div>
        ) : (
          <>
            {visible.map((item, i) => (
              <div key={i} className={classes.row}>
                {renderItem(item, i)}
              </div>
            ))}
            {hasMore && (
              <button
                className={classes.loadMoreBtn}
                onClick={() => setVisibleCount((c) => c + pageSize)}
              >
                Load more ({items.length - visibleCount} more) ▼
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
